/**
 * AnimatedMessage - Success/Error message component with slide animations
 * Auto-dismisses success messages after a timeout
 */

import { useEffect, useState } from 'react';

export interface AnimatedMessageProps {
  type: 'success' | 'error';
  message: string;
  onDismiss?: () => void;
  autoDismissDelay?: number; // in milliseconds
  className?: string;
}

export function AnimatedMessage({
  type,
  message,
  onDismiss,
  autoDismissDelay = 5000,
  className = '',
}: AnimatedMessageProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      setIsVisible(false);
      onDismiss?.();
    }, 300); // Match animation duration
  };

  useEffect(() => {
    // Trigger enter animation
    const enterTimer = setTimeout(() => setIsVisible(true), 10);

    // Auto-dismiss for success messages
    let dismissTimer: NodeJS.Timeout;
    if (type === 'success' && autoDismissDelay > 0) {
      dismissTimer = setTimeout(() => {
        handleDismiss();
      }, autoDismissDelay);
    }

    return () => {
      clearTimeout(enterTimer);
      if (dismissTimer) clearTimeout(dismissTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, autoDismissDelay]);

  const baseClasses = `
    rounded-lg p-4 transition-all duration-300 ease-out
    ${isVisible && !isLeaving ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}
  `;

  const typeClasses = {
    success:
      'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800',
    error:
      'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800',
  };

  const iconClasses = {
    success: 'text-green-400',
    error: 'text-red-400',
  };

  const textClasses = {
    success: 'text-green-800 dark:text-green-200',
    error: 'text-red-800 dark:text-red-200',
  };

  return (
    <div
      className={`${baseClasses} ${typeClasses[type]} ${className}`}
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {type === 'success' ? (
            <svg
              className={`h-5 w-5 ${iconClasses[type]}`}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg
              className={`h-5 w-5 ${iconClasses[type]}`}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
        <div className="ml-3 flex-1">
          <p className={`text-sm font-medium ${textClasses[type]}`}>
            {message}
          </p>
        </div>
        {/* Dismiss button for error messages */}
        {type === 'error' && (
          <div className="ml-auto pl-3">
            <button
              type="button"
              onClick={handleDismiss}
              className={`inline-flex rounded-md p-1.5 ${textClasses[type]} hover:bg-red-100 dark:hover:bg-red-800/30 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900`}
              aria-label="Dismiss"
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
